import { showToast, TOAST } from '@mimir/web/eventBus';
import { axiosErrorStatusCode } from '@mimir/common/util/httpError';
import { getGlobalStore } from '@mimir/web/store';
import { getItems } from '@mimir/web/api/globalClientGetItems';
import { getItem, postDeduplicationRequest } from '@mimir/web/util/api';
import { isItemVisibleToUser }  from '@mimir/web/util/security';
import type { SyncItemInfo, NleClipInfo, NleMimirItemInfo, NleClipInfoMap } from '@mimir/client-common/fileSync/premiereTypes';
import type { SequenceExportInfo } from '@mimir/primitive/nle/sequence';
import type { CoreNleClipInfo } from '@mimir/primitive/nle/projectItem';
import { syncItemFieldList } from '@mimir/client-common/fileSync/premiereTypes';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference } from '@mimir/primitive/artifactReference';
import type { Subclip } from '@mimir/client-common/model/subclip';
import type { DownloadSyncOp } from '@mimir/client-common/upload/premiere';
import {
  getLocalMediaStatesForClip,
  getSequenceExportInfoWith,
  highresNeedsToBeDownloaded,
  mediaNeedsRelink,
  proxyNeedsToBeDownloaded,
  shouldSkipPathRewriteUnlessNecessaryWith,
  isClipBroken,
  relinkAllClipsWith,
  syncClipsWithRelink,
  syncClipsWithDownload,
} from '@mimir/client-common/upload/premiere';
import { getRouterLink } from '@mimir/client-common/util/basicRoute';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import { isDeleting } from '@mimir/common/dto/itemModels';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { isValidGUID, type CompleteType } from '@mimir/common/util/general';
import { invariant } from '@mimir/common/util/invariant';
import { getLocalFileSize, isFileAvailableLocally } from '@mimir/common/util/fs';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { addEventListener } from './cep';
import { getItemWithSourceMedia } from './localState';
import { parseMimirIdFromFilePath } from '@mimir/common/util/path';
import { Coalesce } from '@mimir/common/async/coalesce';
import type { ProjectItemInfo } from '@mimir/primitive/nle/projectItem';
import type { NleProject } from '@mimir/primitive/nle/project';
import { Piggyback } from '@mimir/common/async/taskTracking';
import { notNull } from '@mimir/common/portable/basicTypes';
import { projectItemArtifactReference } from '@mimir/client-common/fileSync/premiereInterpret';
import { deriveArtifactReferenceFromItem, resolveArtifactReference } from '@mimir/client-common/fileSync/resolveArtifactReference';
import { isPremiereMimirItemInfoPlaceholder } from '@mimir/client-common/util/premiere';
import { getBasename } from '@mimir/common/platformAbstraction/pathComposition';
import { showSimpleDialog, showSimpleDialogFromRequest } from '@mimir/web/dialogs/simple';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import { clipInfosArray, premiere, setPremiereClipInfos, updatePremiereClipInfo } from '../globals/premiere';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { ICepBridgeClient } from '@mimir/primitive/premiere/cepBridgeClientType';
import type { ArchiveConfig } from '@mimir/common/dto/tenantModel';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import { exceptionToString } from '@mimir/common/util/exception';
import type { Logger } from '@mimir/common/types/logger';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalMediaStateWithMediaDestination, LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { showDownloadPremiereMediaDialog } from '../dialogs/downloadPremiereMedia';
import type { DownloadPremiereMediaDialogRequest, PremiereDownloadParams } from '../dialogs/downloadPremiereMediaDialogRequest';
import { AggregateDownloadInfo } from './clipDownload';
import { makeDownloadOperation } from './makeSyncOp';
import { getOngoingDownloadId } from '@mimir/client-common/model/ongoingDownload';
import { StreamRoleId, type MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import { wasHighresDeleted } from '@mimir/common/util/item';
import type { ProjectOps } from '@mimir/client-common/fileSync/projectOps';
import type { DownloadAndRelinkOps } from '@mimir/client-common/fileSync/downloadAndRelinkOps';
import { DefaultKeldaPathOps } from './defaultKeldaPathOps';
import { DefaultLocalStateOps } from '@mimir/client-common/fileSync/defaultLocalStateOps';
import { DefaultNotificationOps } from './defaultNotificationOps';
import { DefaultOngoingDownloadOps } from './defaultOngoingDownloadOps';
import { DefaultExportOps } from './defaultExportOps';
import type { NleProjectItemReference } from '@mimir/primitive/nle/reference';
import type { SimpleDialogRequest } from '../dialogs/simpleDialogRequest';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import { getSimilarItemsSearchString } from '@mimir/common/search/similarItemsSearch';
import type { ColdStorageRestoreOps } from '@mimir/client-common/coldStorage/coldStorageRestoreOps';
import { setLocalMediaState } from '../globals/localMediaState';
import type { DownloadIdentity } from '@mimir/client-common/fileSync/downloadIdentity';
import { getExtensionFromFileName } from '../model/getExtensionFromFileName';
import { getClipKey } from '@mimir/primitive/nle/clip';
import {
  MEDIA_DURATION_KEY,
  MEDIA_TIMEBASE_KEY,
  MIMIR_ARTIFACT_KEY,
  MIMIR_ITEM_ID_KEY,
  MIMIR_MOGRT_PARENT_ITEM_ID_KEY,
  SUBCLIP_DURATION_KEY,
  TAPE_NAME_KEY,
} from '@mimir/primitive/nle/metadata';
import { NleEventType } from '@mimir/primitive/nle/event';
import { ScanTiming } from './syncTiming';
import { parseBridgeLogEntry } from './bridgeLogEntry';
import { createReactiveDialogRequest } from '@mimir/web/dialogs/requestDialog';

let projectChangedEventHandlersRegistered = false;

const getOpenProjects = async (applicationContext: AdobePanelContext | ElectronContext): Promise<NleProject[]> => {
  return await applicationContext.cepBridgeClient.call('getOpenProjects');
};

const getActiveSequenceSummary = async (applicationContext: AdobePanelContext | ElectronContext): Promise<{ id: string; name: string } | undefined> => {
  return await applicationContext.cepBridgeClient.call('getActiveSequenceSummary');
};

function isProxyOnlyClip(clipInfo: NleClipInfo): boolean {
  if (clipInfo.artifactRef  === null) {
    return false;
  }
  return isProxyOnlyArtifactReference(clipInfo.artifactRef);
}

function anyProxyOnlyClips(clipInfos: NleClipInfo[]): boolean {
  return clipInfos.some((clipInfo) => isProxyOnlyClip(clipInfo));
}

export async function checkOpenProjectsOnce(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    backendClient: BackendClient,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    isStandaloneMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
    skippedFileExtensions: string[],
  },
  nleClipInfos: NleClipInfoMap,
) {
  const newOrderedProjects = getOrderedOpenProjects(await getOpenProjects(services.applicationContext));
  const oldOrderedProjects = premiere.orderedOpenProjects;
  // When switching between projects the 'isCurrentlyActive' property value changes
  // but the projects are actually the same, so we should consider equality check
  // without the 'isCurrentlyActive' property.
  if (!isEqual(
    newOrderedProjects.map(n => ({...n, isCurrentlyActive: undefined})),
    oldOrderedProjects.map(o => ({...o, isCurrentlyActive: undefined}))
  )
  ) {
    const projectChangeDetectedTime = new Date();
    const timing = new ScanTiming();
    premiere.orderedOpenProjects = newOrderedProjects;
    // If newOrderedProjects decreases in length, we decide that a project is closed, so that we don't need to offer downloads.
    // (Usually Premiere) If newOrderedProjects increases in length, we decide that a new project is opened, and offer download for that one.
    // (Usually DVR) If length stays same, it means the single project was closed and another one opened. We offer download for that single one.
    const newProjectPath = newOrderedProjects.length >= oldOrderedProjects.length ? newOrderedProjects[newOrderedProjects.length - 1].path : undefined;
    await refreshPremiereClipInfos(services, config.skippedFileExtensions);
    timing.mark('refreshPremiereClipInfos');
    if (newProjectPath) {
      const newClipInfos = clipInfosArray.value.filter(clipInfo => clipInfo.projectPath === newProjectPath);
      // Auto relink new clips
      await relinkAllClipsWith(services, newClipInfos, config.isProxyMode, premiere);
      timing.mark('relinkAllClips');
      console.log(`Initial processing for ${newProjectPath} took: ${(new Date().getTime() - projectChangeDetectedTime.getTime()) / 1000} seconds`);
      timing.report(services.logger, 'initialProcessing', { projectPath: newProjectPath, clipsInProject: newClipInfos.length, clipsTotal: clipInfosArray.value.length });
      if (!config.isProxyMode && anyProxyOnlyClips(newClipInfos)) {
        showSimpleDialog({
          infoOnly: true,
          maxWidth: 400,
          icon: 'announcement',
          header: 'Proxy mode only',
          baseText: 'Some of the clips in the premiere project have been imported with proxy-only optimizations enabled. You must enable proxy workflow to be able to work on this project',
          actionText: 'Ok',
        });
      } else {
        void suggestDownloads(services, config, newProjectPath);
      }
    }
  } else if (getGlobalStore().getters.dvrMode) {
    // Since we don't get events from DVR on project change (clip added/removed), we need to
    // refresh clip infos periodically even if project stays the same.
    await refreshPremiereClipInfos(services, config.skippedFileExtensions);
  }

  // since we don't have a reliable event on opening/activating a sequence, we hack by checking
  // just the sequence ID with previously stored info. we skip doing the check with sequence
  // key (more unique with project path), since that could be too intrusive for this frequently
  // polled task (checking sequence project also mutates the project by adding and deleting a
  // custom metadata field)
  const sequenceSummary = await getActiveSequenceSummary(services.applicationContext);
  if (!isEqual(sequenceSummary?.id, premiere.activeSequenceInfo?.sequenceId) || !isEqual(sequenceSummary?.name, premiere.activeSequenceInfo?.sequenceName)) {
    await activeSequenceChangedEventHandler(services.applicationContext, services.logger, nleClipInfos);
  }
}

const projectChangedEventHandler = async (
  applicationContext: AdobePanelContext,
  logger: Logger,
) => {
  premiere.projectChangedEventLastFired = new Date().getTime();

  const store = getGlobalStore();

  // Since this function can be invoked at any time, we need to build fresh
  // services instead of using the same ones
  const notificationOps = new DefaultNotificationOps(applicationContext);
  const keldaPathOps = new DefaultKeldaPathOps(applicationContext, logger, store.getters.keldaPathMappings);
  const ongoingDownloadOps = new DefaultOngoingDownloadOps();
  const localStateOps = new DefaultLocalStateOps(applicationContext, logger, ongoingDownloadOps, keldaPathOps, store.getters.downloadPath);

  const services = {
    applicationContext,
    logger,
    notificationOps,
    ongoingDownloadOps,
    localStateOps,
  };
  await refreshPremiereClipInfos(services, store.getters.tenantConfig.skippedFileExtensionsForPremiere);
};

export const activeSequenceChangedEventHandler = async (
  applicationContext: AdobePanelContext | ElectronContext,
  logger: Logger,
  nleClipInfos: NleClipInfoMap,
) => {
  let result: Partial<SequenceExportInfo> = {};
  try {
    const cepb = applicationContext.cepBridgeClient;
    const seqRef = await cepb.call('getActiveSequence');
    if (seqRef) {
      const notificationOps = new DefaultNotificationOps(applicationContext);
      const exportOps = new DefaultExportOps(applicationContext, logger, getGlobalStore().getters.selectedPresetPath, getGlobalStore().getters.dvrMode);
      const services = {
        applicationContext,
        logger,
        notificationOps,
        exportOps,
      };
      const seqInfo = await getSequenceExportInfoWith(services, nleClipInfos, seqRef, null, true);
      result = seqInfo.exportInfo;
    }
  } catch(e) {
    // no active sequence. do nothing.
  }
  premiere.activeSequenceInfo = result;
};

class ItemBatchInfo {
  constructor(public itemInfos: Record<string, SyncItemInfo>) {
  }
}

const getMimirItemInfo = async (itemId: string, prefetched: ItemBatchInfo | null = null): Promise<CompleteType<NleMimirItemInfo>> => {
  // get originalFileName
  let item: SyncItemInfo | null = null;
  if (prefetched?.itemInfos[itemId]) {
    item = prefetched.itemInfos[itemId];
  } else {
    const queryParams = {
      fields: syncItemFieldList.join(', '),
    };
    item = await getItem(itemId, queryParams);
  }
  if (!item) {
    showToast(`Item could not be fetched: (${itemId}).`, TOAST.ERROR);
    throw new Error(`item could not be fetched: ${itemId}`);
  }

  return {
    id: itemId,
    originalFileName: item.originalFileName,
    itemType: item.itemType,
    link: getRouterLink(item.itemType, { id: item.id }, { returnTo: 'premiere' }),
    archiveInfo: item.archiveInfo,
    mediaSize: item.mediaSize,
    proxySize: item.proxySize,
    visibleTo: item.visibleTo,
    createdBy: item.createdBy,
    ingestSourceFullPath: item.ingestSourceFullPath,
    ingest: item.ingest,
    editProxyFormat: item.editProxyFormat,
    itemState: item.itemState,
    media: item.media,
    highresDeletion: item.highresDeletion,
    isDeleting: item.isDeleting,
    proxy: item.proxy,
    transcodingState: item.transcodingState,
  };
};

const notFoundItemIds = new Set<string>();

export const setMimirItemInfo = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  keys: string[],
  itemId: string,
  prefetched: ItemBatchInfo | null = null,
) => {
  let itemInfo;
  let itemNotFound = false;
  try {
    itemInfo = await getMimirItemInfo(itemId, prefetched);
  } catch (err: unknown) {
    if (axiosErrorStatusCode(err) === 404) {
      services.logger.warn(`Item ${itemId} is not found anymore`);
      itemNotFound = true;
      notFoundItemIds.add(itemId);
    } else {
      services.logger.error(`Error in setMimirItemInfo: ${exceptionToString(err)}`);
    }
  }

  const clipInfos = premiere.clipInfos;
  for (const key of keys) {
    const clipInfo = clipInfos[key] as NleClipInfo;
    if (!clipInfo) {
      continue;
    }

    if (itemInfo) {
      clipInfo.error = undefined;
      clipInfo.mimirItem = itemInfo;
      clipInfo.isNotVisibleToUser = !isItemVisibleToUser(itemInfo.visibleTo, itemInfo.createdBy);
      clipInfo.isInRecycleBin = isDeleting(itemInfo);
      if (isPremiereMimirItemInfoPlaceholder(itemInfo)) {
        await updateClipForPlaceholderItem(services, clipInfo);
      }
      await refreshClipLocalMediaStates(services, clipInfo, true);
    } else if (itemNotFound) {
      services.logger.warn(`Resetting item ID for clip ${clipInfo.name}`);
      clipInfo.error = undefined;
      clipInfo.mimirItemId = undefined;
      await services.applicationContext.cepBridgeClient.call('resetMimirDataOnProjectItem', clipInfo.itemRef);
      if (clipInfo.mimirItemLocalMediaStates.highresState?.isStateRefreshing) {
        services.notificationOps.notifyStopRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.highresState);
      }
      if (clipInfo.mimirItemLocalMediaStates.proxyState?.isStateRefreshing) {
        services.notificationOps.notifyStopRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.proxyState);
      }
    } else {
      clipInfo.error = 'Error getting item info';
    }

    updatePremiereClipInfo(clipInfo);
  }
};

interface PathToUpdate {
  path?: string;
  projectPath?: string;
  mimirItemId?: string;
  mimirItemIdFromPath?: string;
  needsUpdate?: boolean;
}

/*
  Keyed by projectPath + path, never by path alone: multiple open projects can
  link the same file, and evidence gathered from one project's clips (its item
  ID metadata) must never be used to stamp item IDs onto another project's
  clips. Cross-project writes keyed by bare paths have corrupted customer
  projects (issue #21326).
*/
type PathsToUpdateMap = Record<string, PathToUpdate>;

const getPathToUpdateKey = (projectPath: string, filepath: string) => `${projectPath}|${filepath}`;

const mapPathToUpdate = (map: PathsToUpdateMap, pathToUpdate: PathToUpdate) => {
  const { path: filepath, projectPath, mimirItemId } = pathToUpdate;
  if (!filepath || isEmpty(filepath) || !projectPath) {
    return;
  }

  const key = getPathToUpdateKey(projectPath, filepath);
  let cachedPathToUpdate = map[key];
  if (!cachedPathToUpdate) {
    cachedPathToUpdate = map[key] = cloneDeep(pathToUpdate);
  }

  if (!isEmpty(mimirItemId)) {
    cachedPathToUpdate.mimirItemId = mimirItemId;
  } else {
    // try both forward and back slash, since the project may have been last saved in a platform
    // which is different from the one this code is running on right now.
    // NOTE: it will be wrong in the unlikely case that there are forward slashes on a linux path
    // like "/home/andaleeb/mjoll/mimir-wsl/\asdf", which is apparently possible.
    cachedPathToUpdate.mimirItemIdFromPath = parseMimirIdFromFilePath(filepath, '\\') ||  parseMimirIdFromFilePath(filepath, '/');
    cachedPathToUpdate.needsUpdate = true;
  }

  if (pathToUpdate.needsUpdate) {
    cachedPathToUpdate.needsUpdate = true;
  }
};

const processPathsToUpdate = async (applicationContext: AdobePanelContext | ElectronContext, map: PathsToUpdateMap, isProxy: boolean) => {
  for (const pathToUpdate of Object.values(map)) {
    if (pathToUpdate.needsUpdate) {
      // prefer mimirItemId (proper metadata).
      let itemIdToUpdateWith = pathToUpdate.mimirItemId;

      // fallback to mimirItemIdFromPath if needed, but don't keep doing this if it is not found from backend.
      if (!itemIdToUpdateWith && pathToUpdate.mimirItemIdFromPath && !notFoundItemIds.has(pathToUpdate.mimirItemIdFromPath)) {
        itemIdToUpdateWith = pathToUpdate.mimirItemIdFromPath;
      }
      if (itemIdToUpdateWith && pathToUpdate.path && pathToUpdate.projectPath) {
        await applicationContext.cepBridgeClient.call('setMimirItemIdForPath', pathToUpdate.path, isProxy, itemIdToUpdateWith, pathToUpdate.projectPath);
      }
    }
  }
};

export async function refreshAllClipsLocalMediaStates(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  forceRefresh = false,
): Promise<void[]> {
  try {
    premiere.refreshingAllLocalMediaStates = true;
    const allClipInfos = clipInfosArray.value;
    return await Promise.all(allClipInfos.map(clipInfo => refreshClipLocalMediaStates(services, clipInfo, forceRefresh)));
  } finally {
    premiere.refreshingAllLocalMediaStates = false;
  }
}

export const refreshClipLocalMediaStates = async (
  services: {
    notificationOps: NotificationOps,
    localStateOps: LocalStateOps,
  },
  clipInfo: NleClipInfo,
  forceRefresh = false,
) => {
  clipInfo.artifactRef = resolveArtifactReference(
    clipInfo.artifactRef,
    isPremiereMimirItemInfoPlaceholder(clipInfo.mimirItem) ? null : clipInfo.mimirItem,
  );

  const localMediaState = await getLocalMediaStatesForClip(services.localStateOps, clipInfo, forceRefresh);

  if (localMediaState == null) {
    return;
  }

  clipInfo.mimirItemLocalMediaStates = localMediaState;
  updatePremiereClipInfo(clipInfo);

  // Media states are set to 'refreshing = true' initially in the 'processPremiereClipInfos', so here we're setting it to
  // false after refreshing is done.
  if (clipInfo.mimirItemLocalMediaStates.highresState?.isStateRefreshing) {
    services.notificationOps.notifyStopRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.highresState);
  }
  if (clipInfo.mimirItemLocalMediaStates.proxyState?.isStateRefreshing) {
    services.notificationOps.notifyStopRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.proxyState);
  }
};

const coalesceRefreshClipInfos = new Coalesce<void>();
export function refreshPremiereClipInfos(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  skippedFileExtensions: string[],
  forceUpdate: boolean = false,
) {
  return coalesceRefreshClipInfos.runOrCoalesce(() => processPremiereClipInfos(services, skippedFileExtensions, forceUpdate));
}

async function interpretClipInfo(logger: Logger, cepb: ICepBridgeClient, rawInfo: ProjectItemInfo, skippedFileExtensions: string[]): Promise<CoreNleClipInfo | null> {
  try {
    const { projectPath, meta, mediaPath, proxyPath, isOffline, itemRef, name } = rawInfo;

    const artifactRef = projectItemArtifactReference(rawInfo);
    const mimirItemId = meta[MIMIR_ITEM_ID_KEY];
    // Clip might not have artifactRef here because we drop artifactRef for legacy ImplicitReference type,
    // but we can still get mimirItemId from metadata and use it to fetch Mimir item info and later resolve
    // artifact reference from the item info.
    if (!(mediaPath || proxyPath || mimirItemId)) {
      return null;
    }

    // Currupted projects might have a wrong itemId set in artifactReference.
    // 'Mimir.Item.Id' always has a master itemId and artifactReference should have it also.
    if (artifactRef && mimirItemId && artifactRef.itemId !== mimirItemId) {
      artifactRef.itemId = mimirItemId;
      await cepb.call('updateProjectItem', itemRef, {
        meta: {
          [MIMIR_ARTIFACT_KEY]: JSON.stringify(artifactRef),
        },
      });
    }

    const clipFileExtension = getExtensionFromFileName(mediaPath).toLowerCase();
    if (skippedFileExtensions.includes(clipFileExtension)) {
      return null;
    }

    const mediaTimebaseString = meta[MEDIA_TIMEBASE_KEY];     // e.g. "29.97 fps" or "48000 Hz"
    const mediaDurationString = meta[MEDIA_DURATION_KEY];     // e.g. "00:01:10:06" or "00:00:22:15360" or "00;00;41;27"
    const subclipDurationString = meta[SUBCLIP_DURATION_KEY]; // Same format as MediaDuration
    const tapeName = meta[TAPE_NAME_KEY];

    // While creating a subclip in Premiere, if "Restrict Trims To Subclip Boundaries" checkbox is selected,
    // then it the MediaDuration column reports the duration of the subclip instead of the duration of the
    // origin clip. So it does not make sense to search with this duration in this case. Hence the condtion
    // below to only set originClipMediaDurationMs when these two are different.

    const strictSubclip = subclipDurationString && (subclipDurationString === mediaDurationString);

    let originClipMediaDurationMs: number | undefined;
    if (mediaTimebaseString && mediaDurationString && !strictSubclip) {
      const mediaTimebase = parseFloat(mediaTimebaseString);
      const [hours, minutes, seconds, samples] = mediaDurationString.split(/:|;/);    // Seen semicolon being used for some audio files

      // Find seconds for each part
      const samplesS = parseFloat(samples) / mediaTimebase;
      const secondsS = parseFloat(seconds);
      const minutesS = parseFloat(minutes) * 60;
      const hoursS = parseFloat(hours) * 60 * 60;

      // Add up and convert to milliseconds
      originClipMediaDurationMs = (hoursS + minutesS + secondsS + samplesS) * 1000;
    }

    return {
      key: getClipKey(itemRef.itemNodeId, projectPath),
      projectPath,
      name,
      mediaPath,
      proxyPath,
      mimirItemId,
      isOffline,
      itemRef,
      artifactRef,
      originClipMediaDurationMs,
      tapeName,
      mimirMogrtParentItemId: meta[MIMIR_MOGRT_PARENT_ITEM_ID_KEY],
    };
  } catch (ex) {
    /*
      TODO: To be user friendly, we should still present the item in the UI
      along with a problem description, rather than writing this warning to the
      console.
    */
    logger.warn(`Ignoring Premiere project item due to interpretation error ${exceptionToString(ex)}`);
    return null;
  }
}

async function fetchPremiereClipInfosImpl(logger: Logger, cepb: ICepBridgeClient, skippedFileExtensions: string[]): Promise<CoreNleClipInfo[]> {
  const rawInfos = await cepb.call('getAllProjectItemInfos');
  return (await Promise.all(rawInfos.map(info => interpretClipInfo(logger, cepb, info, skippedFileExtensions)))).filter(notNull);
}


const clipInfoPiggyback = new Piggyback<CoreNleClipInfo[]>();
export function fetchPremiereClipInfos(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  skippedFileExtensions: string[],
): Promise<CoreNleClipInfo[]> {
  return clipInfoPiggyback.runOrPiggyback(() => fetchPremiereClipInfosImpl(services.logger, services.applicationContext.cepBridgeClient, skippedFileExtensions));
}

const isSubset = (superset: NleClipInfo, subset: CoreNleClipInfo) => (Object.keys(subset) as (keyof CoreNleClipInfo)[]).every(key => key in superset && isEqual(superset[key], subset[key]));
const coreNleClipInfoChanged = (previousClipInfos: NleClipInfoMap, newClipInfosArray: CoreNleClipInfo[]) => {
  if (Object.keys(previousClipInfos).length !== newClipInfosArray.length) {
    return true;
  }
  for (const newClipInfo of newClipInfosArray) {
    const key = newClipInfo.key;
    const previousClipInfo = previousClipInfos[key];
    if (previousClipInfo && isSubset(previousClipInfo, newClipInfo)) {
      continue;
    }

    return true;
  }

  return false;
};

const processPremiereClipInfos = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  skippedFileExtensions: string[],
  forceUpdate: boolean,
) => {
  try {
    premiere.refreshingAllClipInfos = true;
    return await processPremiereClipInfosInternal(services, skippedFileExtensions, forceUpdate);
  } finally {
    premiere.refreshingAllClipInfos = false;
  }
};

const updateClipForPlaceholderItem = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  updatedClipInfo: NleClipInfo
) => {
  const highresState = updatedClipInfo.mimirItemLocalMediaStates.highresState!;
  const highresPath = highresState.localPath;
  const isAvailableLocally = await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, highresPath);
  highresState.isAvailableLocally = isAvailableLocally;

  if (!highresState.fileSize && isAvailableLocally) {
    highresState.fileSize = getLocalFileSize(services.applicationContext.nodeApi, highresPath!);
  }
  highresState.isStateRefreshing = false;
  updatedClipInfo.mimirItemLocalMediaStates.proxyState!.isStateRefreshing = false;
};

const processPremiereClipInfosInternal = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  skippedFileExtensions: string[],
  forceUpdate: boolean,
) => {
  const timing = new ScanTiming();
  const previousClipInfos = forceUpdate ? {} : premiere.clipInfos;
  const newClipInfosArray = await fetchPremiereClipInfos(services, skippedFileExtensions);
  timing.mark('fetchPremiereClipInfos');
  if (!coreNleClipInfoChanged(previousClipInfos, newClipInfosArray)) {
    // Skip unnecessary processing since nothing has changed.
    // Note: there could be other changes like availability of local files that will not be checked
    // as a result of this early return. But those changes should be handled individually e.g., when
    // completing a download.
    services.logger.debug('processPremiereClipInfos: Skipping since clip info is identical to the previous time.');
    timing.report(services.logger, 'processPremiereClipInfos (unchanged)', { clips: newClipInfosArray.length });
    return;
  }
  timing.mark('coreNleClipInfoChanged');

  const itemIdToKeys: Record<string, string[]> = {};
  const mediaPathsToUpdate: PathsToUpdateMap = {};
  const proxyPathsToUpdate: PathsToUpdateMap = {};

  const newClipInfos: Record<string, NleClipInfo> = {};
  const clipInfoPromises = [];
  const processClipInfo = async (newClipInfo: CoreNleClipInfo) => {
    let updatedClipInfo: NleClipInfo;
    const key = newClipInfo.key;
    const storedRef = newClipInfo.artifactRef;
    if (previousClipInfos[key]) {
      const previousClip = previousClipInfos[key];
      updatedClipInfo = {
        ...previousClip,
        ...newClipInfo,
        artifactRef: storedRef ?? previousClip.artifactRef,
      };
    } else {
      updatedClipInfo = {
        ...newClipInfo,
        mimirItemLocalMediaStates: {},
        mimirItem: null,
        artifactRef: storedRef,
      };
    }

    if (updatedClipInfo.error === undefined) {
      updatedClipInfo.error = '';
    }

    if (updatedClipInfo.mediaPath == updatedClipInfo.proxyPath) {
      /*
        After importing a proxy clip into Premiere, the mediaPath (highRes) is
        still set until the Premiere project has been saved and then reloaded.

        This normalizes the output from the Premiere API so that the result
        looks the same regardless of whether the Premiere project has been
        saved and reloaded.
      */
      updatedClipInfo.mediaPath = '';
    }

    if (!updatedClipInfo.mimirItemLocalMediaStates.highresState) {
      updatedClipInfo.mimirItemLocalMediaStates.highresState = {
        localPath: newClipInfo.mediaPath,
        isStateRefreshing: true,
        isBeingDownloaded: false,
        fileSize: 0,
      };
    }

    if (!updatedClipInfo.mimirItemLocalMediaStates.proxyState) {
      updatedClipInfo.mimirItemLocalMediaStates.proxyState = {
        localPath: newClipInfo.proxyPath,
        isStateRefreshing: true,
        isBeingDownloaded: false,
        fileSize: 0,
      };
    }

    let forceUpdateForPath = false;
    if (!updatedClipInfo.mimirItemId || isPremiereMimirItemInfoPlaceholder(updatedClipInfo.mimirItem)) {
      // needs upload
      const highresState = updatedClipInfo.mimirItemLocalMediaStates.highresState!;
      const highresPath = highresState.localPath = newClipInfo.mediaPath;
      const isAvailableLocally = await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, highresPath);
      highresState.isAvailableLocally = isAvailableLocally;

      if (!highresState.fileSize) {
        if (isAvailableLocally) {
          highresState.fileSize = getLocalFileSize(services.applicationContext.nodeApi, highresPath!);
        }
        highresState.isStateRefreshing = false;
        updatedClipInfo.mimirItemLocalMediaStates.proxyState!.isStateRefreshing = false;
      }

      if (getGlobalStore().getters.itemDeduplicationEnabled) {
        const name = getBasename(highresPath!, services.applicationContext.pathSep);
        const size = highresState.fileSize ?? -1;

        const dedupDto = await postDeduplicationRequest({
          items: [{
            name,
            size,
          }]
        });
        if (dedupDto.items[0]) {
          services.logger.info(`Found existing item ${dedupDto.items[0].id} for file ${name}`);
          updatedClipInfo.mimirItemId = dedupDto.items[0].id;
          forceUpdateForPath = true;
          if (!itemIdToKeys[updatedClipInfo.mimirItemId]) {
            itemIdToKeys[updatedClipInfo.mimirItemId] = [];
          }
          itemIdToKeys[updatedClipInfo.mimirItemId].push(key);
        }
      }
    } else if (updatedClipInfo.isRelinking && !clipNeedsRelink(updatedClipInfo)) {
      // it is a mimir clip and was chosen to relink. now paths are set and indicates relink is done.
      updatedClipInfo.isRelinking = false;
    }

    // If clip has 'Mimir Item Id' metadata set, add it to the list of item IDs to fetch from the server
    if (updatedClipInfo.mimirItemId && (updatedClipInfo.mimirItem === undefined || updatedClipInfo.mimirItem === null || updatedClipInfo.mimirItem.id !== updatedClipInfo.mimirItemId)) {
      // build item to clip map as many clips can refer to same item, so we only need to fetch item info once
      updatedClipInfo.mimirItemLocalMediaStates.highresState!.isStateRefreshing = true;
      updatedClipInfo.mimirItemLocalMediaStates.proxyState!.isStateRefreshing = true;

      const mimirItemId = updatedClipInfo.mimirItemId;

      if (!itemIdToKeys[mimirItemId]) {
        itemIdToKeys[mimirItemId] = [];
      }
      itemIdToKeys[mimirItemId].push(key);
    }

    updatedClipInfo.preferMediaPathInProject = shouldSkipPathRewriteUnlessNecessary(services.applicationContext, newClipInfo.mediaPath) && (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, newClipInfo.mediaPath));

    // paths to update, considering highres and proxy paths reported by Premiere as well
    // as the "ideal" paths according to local download location.
    mapPathToUpdate(mediaPathsToUpdate, {
      path: updatedClipInfo.mimirItemLocalMediaStates.highresState!.localPath,
      projectPath: updatedClipInfo.projectPath,
      mimirItemId: updatedClipInfo.mimirItemId,
      needsUpdate: forceUpdateForPath,
    });
    mapPathToUpdate(mediaPathsToUpdate, {
      path: newClipInfo.mediaPath,
      projectPath: updatedClipInfo.projectPath,
      mimirItemId: updatedClipInfo.mimirItemId,
      needsUpdate: forceUpdateForPath,
    });
    mapPathToUpdate(proxyPathsToUpdate, {
      path: updatedClipInfo.mimirItemLocalMediaStates.proxyState!.localPath,
      projectPath: updatedClipInfo.projectPath,
      mimirItemId: updatedClipInfo.mimirItemId,
      needsUpdate: forceUpdateForPath,
    });
    mapPathToUpdate(proxyPathsToUpdate, {
      path: newClipInfo.proxyPath,
      projectPath: updatedClipInfo.projectPath,
      mimirItemId: updatedClipInfo.mimirItemId,
      needsUpdate: forceUpdateForPath,
    });
    newClipInfos[key] = updatedClipInfo;
  };


  for (const newClipInfo of newClipInfosArray) {
    clipInfoPromises.push(processClipInfo(newClipInfo));
  }

  await Promise.all(clipInfoPromises);
  timing.mark('processClipInfo');

  await processPathsToUpdate(services.applicationContext, mediaPathsToUpdate, false);
  await processPathsToUpdate(services.applicationContext, proxyPathsToUpdate, true);
  timing.mark('processPathsToUpdate');

  setPremiereClipInfos(newClipInfos);
  timing.mark('setPremiereClipInfos');

  // start async update
  const itemIds = Object.keys(itemIdToKeys);
  const prefetched = await fetchItemBatchInfo(itemIds);
  timing.mark('fetchItemBatchInfo');
  await Promise.all(itemIds.map(itemId => setMimirItemInfo(services, itemIdToKeys[itemId], itemId, prefetched)));
  timing.mark('setMimirItemInfo');

  timing.report(services.logger, 'processPremiereClipInfos', {
    clips: newClipInfosArray.length,
    distinctMimirItemIds: itemIds.length,
    mediaPathsToUpdate: Object.keys(mediaPathsToUpdate).length,
    proxyPathsToUpdate: Object.keys(proxyPathsToUpdate).length,
  });
};

export const clipNeedsRelink = (clipInfo: NleClipInfo) => {
  const isProxyEnabled = getGlobalStore().getters.isProxyWorkflowEnabled as boolean;
  return !isProxyEnabled && mediaNeedsRelink(clipInfo, false) || isProxyEnabled && mediaNeedsRelink(clipInfo, true);
};

export const syncFilteredClipsRelink = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    isProxyMode: boolean,
  },
  clipInfos: NleClipInfo[],
) => {
  await refreshAllClipsLocalMediaStates(services);
  await syncClipsWithRelink(services, config, clipInfos, null);
};

export const syncFilteredClipsDownload = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    backendClient: BackendClient,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  clipInfos: NleClipInfo[],
  operation: DownloadSyncOp,
) => {
  // We should not need to refresh clip or local media states here because we should already have them updated.
  await syncClipsWithDownload(services, config, clipInfos, operation);
};

const debouncedProjectChangedEventHandler = debounce(projectChangedEventHandler, 200);
const debouncedActiveSequenceChangedEventHandler = debounce(activeSequenceChangedEventHandler, 200);

export const registerProjectChangedEventHandlers = (
  applicationContext: AdobePanelContext,
  logger: Logger,
  nleClipInfos: NleClipInfoMap,
) => {
  if (!projectChangedEventHandlersRegistered) {
    addEventListener(NleEventType.ProjectChanged, () => debouncedProjectChangedEventHandler(applicationContext, logger));
    addEventListener(NleEventType.ActiveSequenceChanged, () => debouncedActiveSequenceChangedEventHandler(applicationContext, logger, nleClipInfos));
    addEventListener(NleEventType.LogMessage, (msg: any) => {
      const { message, level, ...details } = parseBridgeLogEntry(msg.data);
      const detailsText = isEmpty(details) ? '' : ` ${JSON.stringify(details)}`;
      logger.log({ level: level ?? 'info', message: `[cep] ${message}${detailsText}` });
    });

    projectChangedEventHandlersRegistered = true;
  }
};


const shouldSkipPathRewriteUnlessNecessary = (adobeContext: AdobePanelContext | ElectronContext, filePath: string) => {
  return shouldSkipPathRewriteUnlessNecessaryWith(adobeContext, filePath);
};

/**
 * Fetch the information needed to resolve local file paths for a batch of
 * items.
 *
 * Performs bulk API calls towards the server, rather than one API call per
 * item.
 */
async function fetchItemBatchInfo(itemIds: string[]): Promise<ItemBatchInfo> {
  if (itemIds.length == 0) {
    return new ItemBatchInfo({});
  }

  // We're casting both the DTO types and SyncItemInfo to CompleteTypes here so that the type system will inform us if we are
  // trying to use fields that are unsupported by the getItems endpoint.
  const itemEntries = await getItems(itemIds, syncItemFieldList);

  const itemInfos : Record<string, CompleteType<SyncItemInfo>> = { };
  for (const itemEntry of itemEntries) {
    const itemType = itemEntry.itemType;
    if (itemType === undefined) {
      continue;
    }
    itemInfos[itemEntry.id] = { ...itemEntry, itemType, proxy: itemEntry.proxy ?? null };
  }
  return new ItemBatchInfo(itemInfos);
}

type LocalMediaStateItem = Pick<LoadedItem, 'id' | 'aaf' | 'itemType' | 'virtualClipInfo' | 'originalFileName' | 'media' | 'itemState'>;
export interface SyncLocalMediaStateParams {
  item: LocalMediaStateItem,
  subclip: Subclip | null;
  downloadIdentity: DownloadIdentity,
}

export type StrictSyncLocalMediaStateParams = SyncLocalMediaStateParams & Required<Pick<SyncLocalMediaStateParams, 'item' | 'downloadIdentity'>>;

export async function getSyncedLocalMediaState(
  services: {
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  params: StrictSyncLocalMediaStateParams,
): Promise<LocalMediaStateWithMediaDestination> {
  invariant(params.item, 'Expected params.item');
  const result = await syncLocalMediaState(services, params);
  invariant(result,  'Expected syncLocalMediaState result');
  return result;
}


export async function syncLocalMediaState(
  services: {
    logger: Logger,
    localStateOps: LocalStateOps,
  },
  params: SyncLocalMediaStateParams
): Promise<LocalMediaStateWithMediaDestination> {
  const { item, subclip, downloadIdentity } = params;

  const highresItem = getItemWithSourceMedia(item);
  // Provide a subclip so getItemArtefactLocalMediaState can derive range info when needed
  const localMediaState = await services.localStateOps.getItemArtefactLocalMediaState(downloadIdentity, { ...highresItem }, subclip);
  invariant(localMediaState, 'Expected localMediaState');


  setLocalMediaState(downloadIdentity, localMediaState);
  if (highresItem.id !== item.id) {
    setLocalMediaState(downloadIdentity, localMediaState);
  }
  return { ...localMediaState, isStateRefreshing: false };
}


// Premiere keeps changing the order of projects depending on which project user activates etc.
// so we need to maintain our order internally, since otherwise UI components depending on this
// can rearrange frequently (tabs and filter menu in sync page) disturbing UX.
const getOrderedOpenProjects = (unorderedProjects: NleProject[]) => {
  const newProjectsArray = cloneDeep(unorderedProjects || []);

  // build a map keyed by project path and keep track of the highest order used so far
  let currentMaxOrder = -1;
  const currentProjectsMap: Record<string, NleProject> = {};
  premiere.orderedOpenProjects.forEach(p => {
    currentProjectsMap[p.path] = p;
    if (p.order !== undefined && p.order > currentMaxOrder) {
      currentMaxOrder = p.order;
    }
  });

  // now assign order to the new projects, existing or new
  newProjectsArray.forEach(p => {
    const existingProject = currentProjectsMap[p.path];
    p.order = existingProject ? existingProject.order : ++currentMaxOrder;
  });

  // finally sort new projects with the assigned order
  newProjectsArray.sort((p1, p2) => p1.order! - p2.order!);
  return newProjectsArray;
};


async function suggestDownloads(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    localStateOps: LocalStateOps,
    ongoingDownloadOps: OngoingDownloadOps,
    backendClient: BackendClient,
    logger: Logger,
    notificationOps: NotificationOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    isStandaloneMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  projectPath: string,
): Promise<void> {
  const projectClips = clipInfosArray.value.filter(clipInfo => clipInfo.projectPath === projectPath);

  const downloadInfo = new AggregateDownloadInfo(config.isProxyMode);
  await calculateTotalItemsToDownload(downloadInfo, projectClips, new AbortController().signal);
  const missingFiles = downloadInfo.countDownloads(!config.isProxyMode);
  if (missingFiles > 0) {
    premiere.syncPageSelectedFilterId = `project-${projectPath}`;
    const req: DownloadPremiereMediaDialogRequest = {
      clipInfosArray: projectClips,
      callback: async (response: PremiereDownloadParams | null) => {
        if (response) {
          const dlOp = makeDownloadOperation(config, response.downloadProxy ? 'proxy' : 'highres', response.downloadHighres);
          await syncFilteredClipsDownload(
            services,
            config,
            projectClips,
            dlOp,
          );
        }
      }
    };
    showDownloadPremiereMediaDialog(req);
  }
}


export async function calculateTotalItemsToDownload(
  mutDownloadInfo: AggregateDownloadInfo,
  _clipInfosArray: NleClipInfo[],
  abortSignal: AbortSignal,
) {
  invariant(mutDownloadInfo.isLoadingInfo == false, 'Concurrent calculate not supported');
  mutDownloadInfo.isLoadingInfo = true;

  /*
    Multiple Premiere Project items may refer to the same download artifact. We
    therefore keep track of which download IDs we've already processed, so that
    we do not count them more than once.
  */
  const alreadyCountedIds = new Set<string>();
  function tryAddDownloadId(itemId: string, artefact: ItemArtefact, artifactReference: ArtifactReference) {
    const dlId = getOngoingDownloadId({ itemId, artefact, artifactReference });
    if (alreadyCountedIds.has(dlId)) {
      return false;
    }
    alreadyCountedIds.add(dlId);
    return true;
  }

  try {
    for (const clip of _clipInfosArray) {
      if (abortSignal.aborted) {
        return;
      }

      const itemId = clip.mimirItemId;
      if (itemId == null || isClipBroken(clip)) {
        continue;
      }
      const artifactReference = clip.artifactRef;
      if (artifactReference === null) {
        // artifactRef is null in two cases:
        // 1. mimirItem hasn't been loaded yet (API error or race condition) — nothing to count.
        // 2. mimirItem is a placeholder (clip needs upload, not download) — skip.
        continue;
      }
      const needToDownloadHighres = highresNeedsToBeDownloaded(clip) && tryAddDownloadId(itemId, 'highres', artifactReference);
      const needToDownloadProxy = proxyNeedsToBeDownloaded(clip) && tryAddDownloadId(itemId, 'proxy', artifactReference);
      const isNonVideo = clip.mimirItem?.itemType !== ItemType.VIDEO;
      let highresSize = clip.mimirItem?.mediaSize ?? NaN;
      let proxySize = clip.mimirItem?.proxySize ?? NaN;
      const needsHighres = needToDownloadHighres && !isNonVideo;
      const needsProxy = needToDownloadProxy && !isNonVideo;
      const incrementNonVideo = (needToDownloadHighres || needToDownloadProxy) && isNonVideo;

      if (artifactReference.type == 'TsSegmentRange') {
        const segTimes = artifactReference.segmentTimes;
        const sizeByStreamId = clip.mimirItem?.media?.sizeByStreamId ?? {};
        const segCount = segTimes.last + 1 - segTimes.first;
        highresSize = segCount * (sizeByStreamId[StreamRoleId.LiveRecordHighresTs] ?? NaN);
        proxySize = segCount * (sizeByStreamId[StreamRoleId.LiveRecordWebproxyTs] ?? NaN);
      }
      mutDownloadInfo.updateInfo(
        needsHighres,
        needsProxy,
        incrementNonVideo,
        highresSize,
        proxySize,
        !clip.isNotVisibleToUser && !clip.isInRecycleBin,
        Boolean(clip.mimirItem && wasHighresDeleted(clip.mimirItem))
      );

      // Adding a tiny buffer to avoid blocking the UI
      await asyncTimeout(1);
    }

  } finally {
    mutDownloadInfo.isLoadingInfo = false;
  }
}

export interface ReconnectingServices {
  applicationContext: AdobePanelContext | ElectronContext,
  notificationOps: NotificationOps,
  logger: Logger,
  backendClient: BackendClient,
  ongoingDownloadOps: OngoingDownloadOps,
  localStateOps: LocalStateOps,
  coldStorageRestoreOps: ColdStorageRestoreOps,
  projectOps: ProjectOps,
  downloadAndRelinkOps: DownloadAndRelinkOps,
}

export interface ReconnectingConfig {
  isProxyMode: boolean,
  isStandaloneMode: boolean,
  downloadPath: string,
  archiveConfig: ArchiveConfig | undefined,
  userDataSystemPath: string,
}

export async function connectItemWithConfirmation(
  services: ReconnectingServices,
  config: ReconnectingConfig,
  item: LoadedItem
) {
  const clip = await getSelectedClip(services.applicationContext);
  if (!clip) {
    return;
  }

  const itemTitle = getItemSystemDefaultField(item, SystemDefaultField.Title);
  const allClipInfos = clipInfosArray.value;
  const cachedClipInfo = allClipInfos.find(clipInfo => clipInfo.itemRef.projectRef.path === clip.itemRef.projectRef.path
    && clipInfo.itemRef.itemNodeId === clip.itemRef.itemNodeId);

  if (cachedClipInfo?.mimirItemId === item.id) {
    showToast(`Clip "${clip.name}" is already connected to item "${itemTitle}"`, TOAST.INFO);
    return;
  }

  let confirmationMessage = new SafeHtmlBuilder();
  if (cachedClipInfo?.mimirItemId) {
    confirmationMessage.addText('This clip is already associated with another Mimir item.').addLineBreaks(1);
  }

  confirmationMessage = confirmationMessage.addText('Are you sure you want to connect item ')
        .addBoldText(itemTitle)
        .addText(' to Premiere clip "')
        .addBoldText(clip.name)
        .addText('"?');

  const req = createReactiveDialogRequest<SimpleDialogRequest>({
    loading: false,
    showDialog: true,
    dialogProps: {
      header: 'Connecting item with Premiere clip',
      baseHtml: confirmationMessage,
      actionText: 'Connect',
      cancelText: 'Cancel',
      icon: 'add_link',
      maxWidth: 400,
    },
    callback: async (response: string) => {
      if (response === 'ok') {
        await connectSelectedClipToItem(services, config, item);
        showToast(`Clip "${clip.name}" is now connected to item "${itemTitle}"`, TOAST.INFO);
      }
      req.showDialog = false;
    }
  });

  showSimpleDialogFromRequest(req);
}

async function getSelectedClip(applicationContext: AdobePanelContext | ElectronContext): Promise<ProjectItemInfo | undefined>  {
  const selectedProjectItems = await applicationContext.cepBridgeClient.call('getSelectedProjectItems');
  if (selectedProjectItems.length !== 1) {
    // It was decided that we should avoid doing this for multiple clips at the same
    showToast('Please select a single clip', TOAST.ERROR);
    return;
  }

  const projectItemInfo = selectedProjectItems[0];
  if (!projectItemInfo.mediaPath && !projectItemInfo.proxyPath) {
    // May be a sequence or a special clip like black video, bars and tones, adjustement layer etc.
    showToast('Please select a valid media clip', TOAST.ERROR);
    return;
  }

  return projectItemInfo;
}

async function connectSelectedClipToItem(
  services: ReconnectingServices,
  config: ReconnectingConfig,
  item: LoadedItem
) {
  const clip = await getSelectedClip(services.applicationContext);
  if (!clip) {
    return;
  }

  await connectClipToItem (services.applicationContext, clip.itemRef, item.id, item.media ?? null);
  const key = getClipKey(clip.itemRef.itemNodeId, clip.projectPath);

  // Skip awaiting in order to make it appear quick
  void triggerDownloadAndRelink(services, config, [key]);
}

export async function triggerDownloadAndRelink(
  services: ReconnectingServices,
  config: ReconnectingConfig,
  clipKeysToDownloadAndRelink: string[]
) {
  const store = getGlobalStore();
  // Must force a refresh of clip info right now in order for the syncFilteredClips to work properly having the
  // new item ID and local paths set on it. Otherwise, the event to refresh the clips comes later after we already
  // call syncFilteredClips below. Note: ideally we should only need to refresh those clips but that requires more
  // refactoring.
  await processPremiereClipInfos(services, store.getters.tenantConfig.skippedFileExtensionsForPremiere, false);

  // Get the actual clips now using the keys. Now they should have the proper item references.
  const clipsToDownloadAndRelink = clipKeysToDownloadAndRelink.map(key => premiere.clipInfos[key]);
  const dlOp = makeDownloadOperation(config, config.isProxyMode ? 'proxy' : 'highres', !config.isProxyMode);
  await syncFilteredClipsDownload(
    services,
    config,
    clipsToDownloadAndRelink,
    dlOp,
  );
}

export async function connectClipToItem(applicationContext: AdobePanelContext | ElectronContext, itemRef: NleProjectItemReference, newItemId: string, itemMedia: MediaAvailability | null) {
  if (!isValidGUID(newItemId)) {
    showToast('Invalid format for item ID', TOAST.ERROR);
    return;
  }
  const derivedArtifactReference = deriveArtifactReferenceFromItem({ id: newItemId, media: itemMedia });
  await applicationContext.cepBridgeClient.call('updateProjectItem', itemRef, {
    meta: {
      [MIMIR_ITEM_ID_KEY]: newItemId,
      [MIMIR_ARTIFACT_KEY]: JSON.stringify(derivedArtifactReference),
    },
  });
}

export async function updateClipItemId(applicationContext: AdobePanelContext | ElectronContext, itemRef: NleProjectItemReference, newItemId: string) {
  if (!isValidGUID(newItemId)) {
    showToast('Invalid format for item ID', TOAST.ERROR);
    return;
  }
  await applicationContext.cepBridgeClient.call('updateProjectItem', itemRef, {
    meta: {
      [MIMIR_ITEM_ID_KEY]: newItemId,
    },
  });
}

export async function selectClip(applicationContext: AdobePanelContext | ElectronContext, itemRef: NleProjectItemReference) {
  await applicationContext.cepBridgeClient.call('selectProjectItem', itemRef);
}

export function getClipInfoForSearch(clipInfo: Pick<NleClipInfo, 'mediaPath' | 'mimirItem' | 'originClipMediaDurationMs' | 'tapeName'>) {
  // First, parse the filename from the clip's project metadata.
  let fileName: string | undefined = getBasename(getBasename(clipInfo.mediaPath, '/'), '\\');
  if (fileName) {
    // Check if looks like a Mimir item ID. Any slash here should work since slashes should already be filtered
    // away with getBasename above.
    const possibleMimirItemId = parseMimirIdFromFilePath(fileName, '/');
    if (possibleMimirItemId) {
      // We should then take the file name from originalFileName of the item, since searching with the GUID
      // in the fileName filter is unlikely to return similar items.
      fileName = clipInfo.mimirItem?.originalFileName;
      if (!fileName) {
        // If originalFileName is also not found, e.g. due to item already have been deleted, try tape name.
        fileName = clipInfo.tapeName;
      }
    }
  } else {
    fileName = clipInfo.mimirItem?.originalFileName;
    if (!fileName) {
      fileName = clipInfo.tapeName;
    }
  }

  if (!fileName) {
    return;
  }

  return {
    fileName,
    originClipMediaDurationMs: clipInfo.originClipMediaDurationMs,
  };
}

// Builds search string for items having similar filename and duration
export function getSimilarItemsSearchStringFromClip(clipInfo: Pick<NleClipInfo, 'mediaPath' | 'mimirItem' | 'originClipMediaDurationMs' | 'tapeName'>, durationTolerance: number | undefined) {
  const clipInfoForSearch = getClipInfoForSearch(clipInfo);
  if (!clipInfoForSearch) {
    return;
  }

  return getSimilarItemsSearchString(clipInfoForSearch.fileName, false, clipInfoForSearch.originClipMediaDurationMs, durationTolerance);
}
